import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSave, FiLock, FiSmartphone, FiCheckCircle, FiMail } = FiIcons;

const Settings = () => {
  const [twilioConfig, setTwilioConfig] = useState({
    sid: '',
    token: '',
    phone: ''
  });
  const [notificationEmail, setNotificationEmail] = useState('');
  const [isSaved, setIsSaved] = useState(false);

  // Load Settings
  useEffect(() => {
    const storedSettings = localStorage.getItem('ramirez_settings');
    if (storedSettings) {
      const parsed = JSON.parse(storedSettings);
      setTwilioConfig(parsed.twilio || { sid: '', token: '', phone: '' });
      setNotificationEmail(parsed.email || 'cleanairtruckcheck@gmail.com');
    } else {
        // Defaults
        setTwilioConfig({ sid: 'AC...', token: '...', phone: '+19092842738' });
        setNotificationEmail('cleanairtruckcheck@gmail.com');
    }
  }, []);

  const handleSave = (e) => {
    e.preventDefault();
    
    // Save to Local Storage
    const settings = {
      twilio: twilioConfig,
      email: notificationEmail
    };
    localStorage.setItem('ramirez_settings', JSON.stringify(settings));

    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 3000);
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-2">System Settings</h1>
        <p className="text-gray-400">Configure global application settings and integrations.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* General Settings */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-lg">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-700">
            <div className="p-3 bg-blue-600/20 rounded-lg">
              <SafeIcon icon={FiIcons.FiSettings} className="text-2xl text-blue-500" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">General Preferences</h2>
              <p className="text-xs text-gray-400">Admin Panel Configuration</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="p-4 bg-gray-700/50 rounded-lg">
              <label className="block text-white font-medium mb-2">Admin Notification Email</label>
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiMail} className="text-gray-400" />
                <input 
                  type="email" 
                  value={notificationEmail}
                  onChange={(e) => setNotificationEmail(e.target.value)}
                  className="bg-transparent border-none text-gray-300 w-full focus:ring-0"
                  placeholder="admin@example.com"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">All leads and reminders are routed here.</p>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg">
              <div>
                <h4 className="text-white font-medium">Email Notifications</h4>
                <p className="text-xs text-gray-400">Receive alerts for new leads</p>
              </div>
              <div className="relative inline-block w-12 h-6 transition duration-200 ease-in-out bg-green-500 rounded-full cursor-pointer">
                <span className="absolute left-6 top-1 bg-white w-4 h-4 rounded-full shadow-sm"></span>
              </div>
            </div>
             <div className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg">
              <div>
                <h4 className="text-white font-medium">Dark Mode</h4>
                <p className="text-xs text-gray-400">Always active in Admin</p>
              </div>
              <div className="text-xs text-gray-500 bg-gray-900 px-2 py-1 rounded">Locked</div>
            </div>
          </div>
        </div>

        {/* Twilio Integration */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-lg">
           <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-700">
            <div className="p-3 bg-red-600/20 rounded-lg">
              <SafeIcon icon={FiSmartphone} className="text-2xl text-red-500" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Twilio Integration</h2>
              <p className="text-xs text-gray-400">SMS Gateway Configuration</p>
            </div>
          </div>

          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">Account SID</label>
              <div className="relative">
                <SafeIcon icon={FiLock} className="absolute left-3 top-3.5 text-gray-500" />
                <input 
                  type="text" 
                  value={twilioConfig.sid}
                  onChange={(e) => setTwilioConfig({...twilioConfig, sid: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg pl-10 pr-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm"
                  placeholder="AC..."
                />
              </div>
            </div>
            
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">Auth Token</label>
              <div className="relative">
                <SafeIcon icon={FiLock} className="absolute left-3 top-3.5 text-gray-500" />
                <input 
                  type="password" 
                  value={twilioConfig.token}
                  onChange={(e) => setTwilioConfig({...twilioConfig, token: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg pl-10 pr-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm"
                  placeholder="• • • • • • • •"
                />
              </div>
            </div>

            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">From Number (Twilio)</label>
              <div className="relative">
                <SafeIcon icon={FiSmartphone} className="absolute left-3 top-3.5 text-gray-500" />
                <input 
                  type="text" 
                  value={twilioConfig.phone}
                  onChange={(e) => setTwilioConfig({...twilioConfig, phone: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg pl-10 pr-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm"
                  placeholder="+1909..."
                />
              </div>
            </div>

            <div className="pt-4">
              <button 
                type="submit" 
                className="w-full bg-blue-600 text-white font-bold py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                {isSaved ? (
                  <>
                    <SafeIcon icon={FiCheckCircle} />
                    <span>Settings Saved</span>
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiSave} />
                    <span>Save Credentials</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Settings;